import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { coachFitQuestions, coachFitRun, coachFitChoose } from "@/lib/api";
import { Compass, Loader2, ArrowRight, ArrowLeft, Check, Building2, IndianRupee, AlertTriangle, RefreshCw } from "lucide-react";

/* Find my fit — six questions, three realistic first-job roles. */

export default function FitFinder({ onDone }) {
  const { user, refresh } = useAuth();
  const [qs, setQs] = useState(null);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [chosen, setChosen] = useState(null);

  useEffect(() => { coachFitQuestions().then(({ data }) => { setQs(data.questions); if (data.last_result) setResult(data.last_result); }).catch(() => setQs([])); }, []);

  const run = async () => {
    setBusy(true); setErr("");
    try { const { data } = await coachFitRun(answers); setResult(data); } catch (e) { setErr(e?.response?.data?.detail || "Couldn't work out a fit — try again."); } finally { setBusy(false); }
  };
  const choose = async (role) => {
    setBusy(true);
    try { await coachFitChoose(role); setChosen(role); await refresh?.(); onDone?.(role); } catch (_) {} finally { setBusy(false); }
  };

  if (!qs) return <div className="rounded-3xl border border-stone-200 bg-white p-8 text-stone-500 inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  if (result) return (
    <div data-testid="fit-result">
      <div className="rounded-3xl bg-stone-900 text-stone-50 p-6 md:p-7">
        <p className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Your fit</p>
        <p className="mt-2 text-stone-200 leading-relaxed">{result.summary}</p>
        <button onClick={() => { setResult(null); setI(0); setAnswers({}); }} className="mt-3 text-xs text-stone-400 hover:text-stone-100 inline-flex items-center gap-1"><RefreshCw size={11} /> Retake</button>
      </div>
      <div className="mt-4 grid md:grid-cols-3 gap-4">
        {result.roles.map((r, idx) => {
          const active = chosen === r.role || user?.target_role === r.role;
          return (
            <div key={idx} className={`rounded-3xl border-2 bg-white p-5 flex flex-col ${active ? "border-emerald-400" : idx === 0 ? "border-[#C2410C]/40" : "border-stone-200"}`} data-testid={`fit-role-${idx}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-display text-xl leading-tight">{r.role}</p>
                <span className="shrink-0 text-xs font-mono px-2 py-1 rounded-full bg-amber-50 text-[#C2410C] border border-amber-100">{r.fit_percent}% fit</span>
              </div>
              <p className="text-sm text-stone-700 mt-2 leading-relaxed">{r.why}</p>
              <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-stone-400">First 4 weeks</p>
              <ul className="mt-1 space-y-1 text-sm text-stone-700">{(r.first_4_weeks || []).map((s, j) => <li key={j} className="flex gap-2"><Check size={13} className="mt-1 text-emerald-600 shrink-0" />{s}</li>)}</ul>
              <div className="mt-3 space-y-1 text-xs text-stone-600">
                {r.typical_ctc && <p className="inline-flex items-center gap-1"><IndianRupee size={11} /> Typical fresher pay: <b>{r.typical_ctc}</b></p>}
                {r.tradeoff && <p className="flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5 text-amber-600 shrink-0" /> {r.tradeoff}</p>}
                {(r.matching_drives || []).length > 0 && <p className="flex items-start gap-1"><Building2 size={11} className="mt-0.5 text-[#C2410C] shrink-0" /> At your campus: {r.matching_drives.join(", ")}</p>}
              </div>
              <div className="mt-4 pt-3 border-t border-stone-100">
                {active ? <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-medium"><Check size={14} /> This is my target role</span>
                 : <button onClick={() => choose(r.role)} disabled={busy} data-testid={`fit-choose-${idx}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-stone-50 text-sm font-medium hover:bg-[#C2410C] disabled:opacity-60">Set as my target role <ArrowRight size={13} /></button>}
              </div>
            </div>
          );
        })}
      </div>
      {chosen && <p className="mt-4 text-sm text-stone-700">Target role set to <b>{chosen}</b>. Your plan, skill snapshot and resume builder now tilt toward it. <Link to="/app/coach?tab=plan" className="underline">Open my plan →</Link></p>}
    </div>
  );

  const q = qs[i];
  if (!q) return null;
  const answered = Object.keys(answers).length;
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 md:p-8" data-testid="fit-quiz">
      <div className="flex items-center justify-between gap-3">
        <p className="overline inline-flex items-center gap-2"><Compass size={13} className="text-[#C2410C]" /> Find my fit</p>
        <div className="flex gap-1">{qs.map((_, k) => <span key={k} className={`h-1.5 w-6 rounded-full ${k < i ? "bg-emerald-500" : k === i ? "bg-[#C2410C]" : "bg-stone-200"}`} />)}</div>
      </div>
      <p className="mt-4 text-xs font-mono text-stone-400">Question {i + 1} of {qs.length}</p>
      <h3 className="font-display text-2xl md:text-3xl mt-1">{q.q}</h3>
      <div className="mt-5 grid sm:grid-cols-2 gap-2.5">
        {q.options.map((o) => (
          <button key={o} onClick={() => { setAnswers((a) => ({ ...a, [q.id]: o })); if (i < qs.length - 1) setI(i + 1); }} data-testid="fit-option"
            className={`text-left px-4 py-3 rounded-2xl border text-sm transition-colors ${answers[q.id] === o ? "border-stone-900 bg-stone-900 text-stone-50" : "border-stone-200 hover:border-stone-900"}`}>{o}</button>
        ))}
      </div>
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        {i > 0 && <button onClick={() => setI(i - 1)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900"><ArrowLeft size={14} /> Back</button>}
        {i === qs.length - 1 && <button onClick={run} disabled={busy || answered < 4} data-testid="fit-run" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C2410C] text-white font-semibold hover:bg-[#9a3412] disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Compass size={15} />} {busy ? "Working out your fit…" : "See my fit"}</button>}
        <span className="text-xs text-stone-400">{answered}/{qs.length} answered · honest answers work best</span>
      </div>
      {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
    </div>
  );
}
